import React, { useState, useRef, useEffect } from "react";
import styles from './styles';
import {
  Typography,
  makeStyles,
  Grid,
  Button,
  Divider,
  withStyles,
  Tooltip,
  Link,
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';
import PropTypes from 'prop-types';
import { getI18n } from '../../../../../utils/intl.js'
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { FormTextField, Loading, AlertMessages, TabsContainer, TabPanel } from '../../../../../components';
import {
  createClientDefinition,
  updateClientDefinition,
  deleteClientDefinition,
  resetClientDefinitionEditStatuses,
  fetchWellKnownResponse,
} from '../../../../../actions/authProviders';

import {
  getCreateClientDefinitionError,
  getCreateClientDefinitionStatus,
  getIsCreateClientDefinitionInProgress,
  getUpdateClientDefinitionError,
  getUpdateClientDefinitionStatus,
  getIsUpdatingClientDefinition,
  getDeleteClientDefinitionError,
  getDeleteClientDefinitionStatus,
  getIsDeleteClientDefinitionInProgress,
  getWellKnownResponseError,
  getWellKnownResponseStatus,
  getWellKnownResponseInProgress,
  getWellKnownResponseData,
} from '../../../../../reducers/authProviders/index.js';

import * as CONSTANTS from '../../../../../constants';

const ClientDefinitionDetails = (props) => {
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const {
    definition,
    authProviderUuid,
    onClose,
    creatingDefinitionError,
    createDefinitionStatus,
    isCreatingDefinition,
    updateDefinitionError,
    updateDefinitionStatus,
    isUpdatingDefinition,
    deleteDefinitionError,
    deleteDefinitionStatus,
    isDeletingDefinition,
    createClientDefinition,
    updateClientDefinition,
    deleteClientDefinition,
    resetClientDefinitionEditStatuses,
    wellKnownResponseError,
    wellKnownResponseStatus,
    isFetchingWellKnownResponse,
    wellKnownResponse,
    fetchWellKnownResponse,
  } = props;

  const intl = getI18n(useIntl());
  const [metadata, setMetadata] = useState(definition?.clientMetadata ? JSON.stringify(definition?.clientMetadata, null, 4) : '');
  const [description, setDescription] = useState(definition?.description || '');
  const [name, setName] = useState(definition?.name || '');
  const [isDeleteClicked, setIsDeleteClicked] = useState(false);

  const [notificationMessage, setNotificationMessage] = useState(null);
  const [notificationStatus, setNotificationStatus] = useState(null);
  const [formState, setFormState] = useState({
    errors: {},
    isDirty: false,
  });
  const wellKnownResponseTabId = 'auth-client-defn-response-tab';
  const descriptionTabId = 'auth-client-defn-desc-tab';
  const errorTabId = 'auth-client-defn-err-tab';
  const getErrorTab = () => {
    return {
      id: errorTabId,
      tabId: errorTabId,
      label: intl.getI18nMessage('label.auth.providers.details.client.defn.error.tab'),
      visible: true,
    }
  }
  const tabItems = [
    {
      id: wellKnownResponseTabId,
      tabId: wellKnownResponseTabId,
      label: intl.getI18nMessage('label.auth.providers.details.client.defn.resp.tab'),
      visible: true,
    },
    {
      id: descriptionTabId,
      tabId: descriptionTabId,
      label: intl.getI18nMessage('label.auth.providers.details.client.defn.desc.tab'),
      visible: true,
    },
  ];
  const getTabs = (tabItems) => tabItems.filter(({ visible }) => visible);
  const [tabs, setTabs] = useState(getTabs(tabItems));
  const [currentTab, setCurrentTab] = useState(tabs[0].tabId);

  const timer = useRef(null);
  const delayedValidatorAndSetter = (field, value, setter, validationFunction) => {
    if (timer.current) {
      clearTimeout(timer.current);
    }
    if (setter) {
      setter(value);
    }
    if (validationFunction) {
      timer.current = setTimeout(() => {
        const isValid = validationFunction(value);
        setFormState((formState) => {
          return {
            ...formState,
            isDirty: true,
            errors: {
              ...formState.errors,
              [field]: isValid ? null : `${field} ${intl.getI18nMessage('label.auth.providers.details.invalid.value.message')}`,
            },
          }
        });
      }, 750);
    }
  };

  const ROW_COUNT = 25;
  const isCrudInProgress = isCreatingDefinition || isUpdatingDefinition
    || isDeletingDefinition || isFetchingWellKnownResponse;
  const crudStatus = createDefinitionStatus || updateDefinitionStatus || deleteDefinitionStatus;

  const errorType = useRef({});
  const [serverFieldErrors, setServerFieldErrors] = useState([]);
  const [clientMetadataError, setClientMetadataError] = useState('');
  serverFieldErrors;
  const CLIENT_METADATA_FIELD = 'clientMetadata';

  useEffect(() => {
    setNotificationStatus(null);
    setNotificationMessage(null);
    fetchWellKnownResponse(authProviderUuid);
    // clear off all the redux state for client definitions
    return () => {
      resetClientDefinitionEditStatuses();
    };
  }, []);

  useEffect(() => {
    if (wellKnownResponseStatus === CONSTANTS.FAIL) {
      setNotificationStatus('error');
      setNotificationMessage(wellKnownResponseError || intl.getI18nMessage('label.auth.providers.details.wellknown.response.unknown.error'));
    }
  }, [wellKnownResponseStatus]);

  useEffect(() => {
    if (deleteDefinitionStatus === CONSTANTS.SUCCESS) {
      setNotificationStatus(CONSTANTS.ALERT_SUCCESS);
      setNotificationMessage(intl.getI18nMessage('label.auth.providers.details.sample.client.definition.delete.success.message'));
      // Initiate refresh call for the table in the client definitions tab
      setTimeout(() => {
        onClose(true);
      }, 2500);
    } else if (deleteDefinitionStatus === CONSTANTS.FAIL) {
      setNotificationStatus('error');
      setNotificationMessage(deleteDefinitionError || intl.getI18nMessage('label.unknown.error'));
    }
  }, [deleteDefinitionStatus]);

  useEffect(() => {
    if (createDefinitionStatus === CONSTANTS.SUCCESS) {
      setNotificationStatus(CONSTANTS.ALERT_SUCCESS);
      setNotificationMessage(intl.getI18nMessage('label.auth.providers.details.sample.client.definition.create.success.message'));
      // Initiate refresh call for the table in the client definitions tab
      setTimeout(() => {
        onClose(true);
      }, 2500);
    } else if (createDefinitionStatus === CONSTANTS.FAIL) {
      handleClientDefinitionSaveError(creatingDefinitionError);
      setServerFieldErrors(getSeverErrorFields(updateDefinitionError));
    }
  }, [createDefinitionStatus]);

  useEffect(() => {
    if (updateDefinitionStatus === CONSTANTS.SUCCESS) {
      setNotificationStatus(CONSTANTS.ALERT_SUCCESS);
      setNotificationMessage(intl.getI18nMessage('label.auth.providers.details.sample.client.definition.update.success.message'));
      // Initiate refresh call for the table in the client definitions tab
      setTimeout(() => {
        onClose(true);
      }, 2500);
    } else if (updateDefinitionStatus === CONSTANTS.FAIL) {
      handleClientDefinitionSaveError(updateDefinitionError);
      setServerFieldErrors(getSeverErrorFields(updateDefinitionError));
    }
  }, [updateDefinitionStatus]);

  const handleClientDefinitionSaveError = (saveError) => {
    errorType.current.value = null;
    let err = null;
    if (Array.isArray(saveError) && saveError.length) {
      err = getErrorMessage(saveError);
    } else {
      err = saveError;
    }
    setNotificationStatus('error');
    if (errorType.current.value && errorType.current.value === CLIENT_METADATA_FIELD) {
      // set error to be shown in error tab
      setClientMetadataError(err);
      // setup snackbar to show more info link
      setNotificationMessage(
        appendGetMoreInfoLink(
          intl.getI18nMessage('label.auth.providers.details.sample.client.definition.metadata.invalid'),
        ));
    } else {
      // clear the error tab
      setTabs(getTabs(tabItems));
      //clear the content of the error tab
      setClientMetadataError(null);
      // set single line error in snackbar
      setNotificationMessage(err || intl.getI18nMessage('label.unknown.error'));
    }
  }

  const clearClientMetadataError = () => {
    errorType.current.value = null;
    setClientMetadataError(null);
    setTabs(getTabs(tabItems));
    setCurrentTab(wellKnownResponseTabId);
  }

  const appendGetMoreInfoLink = (message) => <p>{`${message}`} {getMoreInfoLink(onClickGetMoreInfoLink)}</p>
  const getMoreInfoLink = (onClickHandler) =>
      <Link variant="inherit" href="#" onClick={onClickHandler}>
        {intl.getI18nMessage('label.auth.providers.details.sample.client.definition.metadata.invalid.moreinfo')}
      </Link>
    
  const onClickGetMoreInfoLink = () => {
    // on click of more info link add the error tab to list of
    // tabs to be shown
    setTabs([
      ...getTabs(tabItems),
      getErrorTab(),
    ]);
    // select the error tab to make the error content visible
    setCurrentTab(errorTabId);
    // close the snackbar as the error tab is now visible
    closeAlert();
  }

  const getSeverErrorFields = (message) => {
    const fields = [];
    if (Array.isArray(message) && message?.length) {
      if (message.length > 1) {
        message.map((item) => {
          fields.push(item?.field);
        });
      } else {
        fields.push(message[0].field);
      }
    }
    return fields;
  }

  const markFormIsDirty = () => {
    setFormState((formState) => {
      return {
        ...formState,
        isDirty: true,
      }
    })
  }

  const validateField = (regex, value, field) => {
    let isValid = false;
    if (!value || !value.trim().length) {
      setFormState((formState) => {
        return {
          ...formState,
          isDirty: true,
          errors: {
            ...formState.errors,
            [field]: `${field} is empty`,
          },
        }
      });
    } else if (regex?.test(value)) {
      setFormState((formState) => {
        return {
          ...formState,
          isDirty: true,
          errors: {
            ...formState.errors,
            [field]: `${field} ${intl.getI18nMessage('label.auth.providers.details.invalid.chars.message')}`,
          },
        }
      });
    } else {
      setFormState((formState) => {
        return {
          ...formState,
          isDirty: true,
          errors: {
            ...formState.errors,
            [field]: null,
          },
        }
      });
      isValid = true;
    }
    return isValid;
  };

  const isJsonValid = (str) => {
    let isValid;
    try {
      isValid = Boolean(JSON.parse(str));
    } catch (error) {
      isValid = false;
    }
    return isValid;
  }

  const onSaveHandler = () => {
    closeAlert();
    clearClientMetadataError();
    const payloadToSave = {
      ...definition,
      authProviderUuid,
      clientMetadata: JSON.parse(metadata),
      description: description,
      name: name,
    };
    // remove unwanted fields set by ui table
    delete payloadToSave.selected;
    delete payloadToSave.numSelectedRows;
    delete payloadToSave.expanded;
    delete payloadToSave.disabled;
    // call save or update accordingly
    if (definition?.uuid) {
      updateClientDefinition(payloadToSave, authProviderUuid, definition?.uuid, tabs.length === 3);
    } else {
      createClientDefinition(payloadToSave, authProviderUuid, tabs.length === 3);
    }
  }

  const handleDeleteClientDefinition = () => {
    closeAlert();
    deleteClientDefinition(authProviderUuid, definition?.uuid);
  }

  const canSave = () => {
    return formState.isDirty &&
      (Boolean(name) && !formState.errors.name) &&
      (Boolean(metadata) && !formState.errors.metadata);
  }

  const handleChangeInName = (value) => {
    setName(value);
    validateField(null, value, 'name');
  }

  const handleChangeInMetadata = (value) => {
    delayedValidatorAndSetter('metadata', value, setMetadata, isJsonValid)
  }

  const handleChangeInDescription = (value) => {
    setDescription(value);
    markFormIsDirty();
  }

  const handleTabChange = (e, newValue) => {
    setCurrentTab(tabs[tabs.findIndex((v) => v.id === newValue)].tabId);
  };

  const closeAlert = () => {
    setNotificationMessage(null);
    setNotificationStatus(null);
  }

  const onCloseHandler = () => {
    closeAlert();
    if (onClose) {
      onClose();
    }
  }

  const getErrorMessage = (message) => {
    let errors = null;
    errorType.current.value = null;
    if(message.length === 1 && !message[0].field?.startsWith(CLIENT_METADATA_FIELD)) {
      errors = message[0].field ? `${message[0].field} : ${message[0].error}` : `${message[0].error}`;
    } else if (message.length >= 1) {
      errors = <ul>{message.map((item, index) => {
      // differentiate between clientMetadata & other errors
      if (item.field) {
        if (item.field.startsWith(CLIENT_METADATA_FIELD)) {
          errorType.current.value = CLIENT_METADATA_FIELD;
        }
        return (
            <li key={index}>
              <b>{item?.field ? `${item.field} : ${item.error}` : `${item.error}`}</b>
            </li>
          );
        }
      })}</ul>;
    }
    return errors;
  }

const header = (
  <Grid container className={classes.pageTitle} direction={'row'} justifyContent={'space-between'}>
    {notificationMessage &&
      <AlertMessages
        id={'auth-client-def-alert'}
        variant={notificationStatus}
        message={notificationMessage}
        containerClass={errorType.current.value === CLIENT_METADATA_FIELD
          ? classes.moreInfoAlertMessage : classes.messageContainer
        }
        onClose={closeAlert}
        hasNotificationIcon={errorType.current.value === CLIENT_METADATA_FIELD}
      />
    }
    <Grid item>
      <Grid container direction={'row'} spacing={1}>
        <Grid item>
          <Typography variant="h4">
            {intl.getI18nMessage('label.auth.providers.details.sample.client.definitions.add')}
          </Typography>
        </Grid>
        {definition?.uuid ? (
          <Grid item>
            {isDeleteClicked ?
              <div className={classes.deleteConfirmContainer}>
                <Button
                  id="delete-confirm-button"
                  data-apim-test="delete-confirm-button"
                  className={classes.errorBackgroundButton}
                  onClick={() => {
                    setIsDeleteClicked(false);
                    handleDeleteClientDefinition();
                  }
                  }
                >
                  {intl.getI18nMessage('label.details.header.delete.confirm.status.button')}
                </Button>
                <Button
                  id="delete-cancel-button"
                  data-apim-test="delete-cancel-button"
                  color="primary"
                  variant="outlined"
                  className={classes.cancelIcon}
                  onClick={() => setIsDeleteClicked(false)}
                >
                  {intl.getI18nMessage('label.details.header.delete.cancel.delete')}
                </Button>
              </div> :
              <Button
                id="delete-button"
                data-apim-test="delete-button"
                variant="outlined"
                className={classes.deleteButton}
                disabled={!authProviderUuid || crudStatus === CONSTANTS.SUCCESS}
                onClick={() => setIsDeleteClicked(true)}
              >
                {intl.getI18nMessage('label.details.header.delete')}
              </Button>
            }
          </Grid>) : null
        }
      </Grid>
    </Grid>
    <Grid item>
      <IconButton
        className={classes.closeIcon}
        size="small" edge="start" color="secondary"
        id={"auth-client-def-close-icon"}
        onClick={onCloseHandler}>
        <CloseIcon />
      </IconButton>
    </Grid>
  </Grid>
);

const body = (
  <Grid container spacing={1} alignItems={'stretch'} justifyContent={'space-between'} >
    <Grid item xs={6}>
      <FormTextField
        id='auth-provider-client-defn-name-field'
        name={intl.getI18nMessage(
          'label.auth.providers.details.sample.client.definitions.name',
        )}
        helperText={intl.getI18nMessage(
          'label.auth.providers.details.sample.client.definition.name.footnote',
        )}
        handleChange={handleChangeInName}
        value={name}
        maxLength={CONSTANTS.AUTH_CLIENT_DEFINITION_NAME_MAX_LENGTH}
        disabled={isCrudInProgress || crudStatus === CONSTANTS.SUCCESS}
        error={serverFieldErrors?.some((field)=> field?.trim()?.toLowerCase()?.startsWith('name'))}
      />
      <FormTextField
        id='auth-provider-client-defn-metadata-field'
        name={
          <div className={classes.helpIconContainer}>
            <div>
              {intl.getI18nMessage('label.auth.providers.details.sample.client.definition.metadata')}
            </div>
            <Tooltip title={intl.getI18nMessage('label.auth.providers.details.sample.client.definition.metadata.tooltip')} arrow placement="right">
              <HelpIcon className={classes.helpIcon} />
            </Tooltip>
          </div>
        }
        helperText={intl.getI18nMessage(
          'label.auth.providers.details.sample.client.definition.metadata.footnote',
        )}
        handleChange={handleChangeInMetadata}
        maxLength={CONSTANTS.AUTH_CLIENT_DEFINITION_METADATA_MAX_LENGTH}
        value={metadata}
        rows={ROW_COUNT}
        multiline={true}
        fieldContainerClass={classes.textFieldContainer2}
        placeholder={'{}'}
        error={formState.errors['metadata'] || serverFieldErrors?.some((field)=> field?.trim()?.toLowerCase()?.startsWith(CLIENT_METADATA_FIELD.toLowerCase()))}
        errorHelperText={formState.errors['metadata']}
        disabled={isCrudInProgress || crudStatus === CONSTANTS.SUCCESS}
      />
    </Grid>
    <Grid item xs={6}>
      <Grid container className={classes.gridContainer}>
        <TabsContainer
          id="auth-provider-client-defns-tab-container"
          tabValue={currentTab}
          orientation="horizontal"
          tabItems={tabs}
          handleTabChange={handleTabChange}
          tabActiveClass={classes.tabActive}
          tabIndicatorClass={classes.tabIndicator}
          useTabId
        />
      </Grid>
      <Divider className={classes.divider} />
      <Grid container className={classes.bodyContent}>
        <TabPanel visible={currentTab === wellKnownResponseTabId}
          id={wellKnownResponseTabId}>
          <FormTextField
            id='auth-provider-client-defn-resp-field'
            fieldContainerClass={classes.textFieldContainer3}
            value={wellKnownResponse ? JSON.stringify(wellKnownResponse, null, 4) : ''}
            rows={ROW_COUNT}
            multiline={true}
            disabled={true}
            name={intl.getI18nMessage('label.auth.providers.details.client.defn.resp')}
          />
        </TabPanel>
        <TabPanel visible={currentTab === descriptionTabId} id={descriptionTabId}>
          <FormTextField
            id='auth-provider-client-defn-desc-field'
            fieldContainerClass={classes.textFieldContainer}
            name={
              <div className={classes.helpIconContainer}>
                <div>
                  {intl.getI18nMessage('label.auth.providers.details.sample.client.definition.metadata.desc')}
                </div>
                <Tooltip
                  title={
                    intl.getI18nMessage('label.auth.providers.details.sample.client.definition.metadata.desc.tooltip')
                  }
                  arrow
                  placement="right"
                >
                  <HelpIcon className={classes.helpIcon} />
                </Tooltip>
              </div>
            }
            helperText={intl.getI18nMessage(
              'label.auth.providers.details.sample.client.definition.metadata.desc.footnote')}
            handleChange={handleChangeInDescription}
            value={description}
            maxLength={CONSTANTS.AUTH_CLIENT_DEFINITION_DESC_MAX_LENGTH}
            rows={ROW_COUNT}
            multiline={true}
            disabled={isCrudInProgress || crudStatus === CONSTANTS.SUCCESS}
            optional
          />
        </TabPanel>
        <TabPanel visible={currentTab === errorTabId} id={errorTabId}>
          <div
            className={classes.errorContainer}
            id='error-container'
            data-apim-test='error-container'
          >
            <Grid container direction="row">
              {clientMetadataError}
            </Grid>
          </div>
        </TabPanel>
      </Grid>
    </Grid>
  </Grid>
);
const footer = (
  <Grid className={classes.pageFooter} container spacing={1} direction={'row'} justifyContent={'flex-end'}>
    <Grid item xs={9}>
      {tabs.length === 3 ? (
      <div className={classes.moreInfoFootNote}>
        <WarningIcon className={classes.errorIcon} />
        <div>
          {intl.getI18nMessage("label.auth.providers.details.sample.client.definition.metadata.force.error.text")}
        </div>
      </div>):null}
    </Grid>
    <Grid item>
      <Button
        id="save-button"
        data-apim-test="save-button"
        variant="contained"
        color={"secondary"}
        className={tabs.length === 3 ? classes.saveButtonForce:classes.saveButton}
        onClick={onSaveHandler}
        disabled={!canSave() || isCrudInProgress || crudStatus === CONSTANTS.SUCCESS}
      >
        {tabs.length === 3?
        intl.getI18nMessage("label.auth.providers.details.sample.client.definition.metadata.force.save")
        :intl.getI18nMessage("label.save.button")}
      </Button>
    </Grid>
    <Grid item>
      <Button
        id="cancel-button"
        data-apim-test="cancel-button"
        color="primary"
        variant="outlined"
        className={classes.cancelButton}
        onClick={onCloseHandler}
        disabled={isCrudInProgress || crudStatus === CONSTANTS.SUCCESS}
      >
        {intl.getI18nMessage("label.details.header.delete.cancel.delete")}
      </Button>
    </Grid>
  </Grid>
);

return (
  <div className={classes.pageSettings}>
    {isCrudInProgress && <Loading pageLoader />}
    {header}
    {body}
    {footer}
  </div>
);
}

ClientDefinitionDetails.propTypes = {
  resetClientDefinitionEditStatuses: PropTypes.func,
  creatingDefinitionError: PropTypes.string,
  createDefinitionStatus: PropTypes.string,
  isCreatingDefinition: PropTypes.bool,
  updateDefinitionError: PropTypes.string,
  updateDefinitionStatus: PropTypes.string,
  isUpdatingDefinition: PropTypes.bool,
  deleteDefinitionError: PropTypes.string,
  deleteDefinitionStatus: PropTypes.string,
  isDeletingDefinition: PropTypes.bool,
  createClientDefinition: PropTypes.func,
  updateClientDefinition: PropTypes.func,
  deleteClientDefinition: PropTypes.func,
  fetchWellKnownResponse: PropTypes.func,
  wellKnownResponseError: PropTypes.string,
  wellKnownResponseStatus: PropTypes.string,
  isFetchingWellKnownResponse: PropTypes.bool,
  wellKnownResponse: PropTypes.object,
  onClose: PropTypes.func,
  authProviderUuid: PropTypes.string,
  definition: PropTypes.object,
};

const mapStateToProps = state => ({
  // isPortalAdmin: getIsPortalAdmin(state),
  creatingDefinitionError: getCreateClientDefinitionError(state),
  createDefinitionStatus: getCreateClientDefinitionStatus(state),
  isCreatingDefinition: getIsCreateClientDefinitionInProgress(state),
  updateDefinitionError: getUpdateClientDefinitionError(state),
  updateDefinitionStatus: getUpdateClientDefinitionStatus(state),
  isUpdatingDefinition: getIsUpdatingClientDefinition(state),
  deleteDefinitionError: getDeleteClientDefinitionError(state),
  deleteDefinitionStatus: getDeleteClientDefinitionStatus(state),
  isDeletingDefinition: getIsDeleteClientDefinitionInProgress(state),
  wellKnownResponseError: getWellKnownResponseError(state),
  wellKnownResponseStatus: getWellKnownResponseStatus(state),
  isFetchingWellKnownResponse: getWellKnownResponseInProgress(state),
  wellKnownResponse: getWellKnownResponseData(state),
});

const mapDispatchToProps = {
  createClientDefinition,
  updateClientDefinition,
  deleteClientDefinition,
  resetClientDefinitionEditStatuses,
  fetchWellKnownResponse,
};
export default (
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(ClientDefinitionDetails);
